import { LitElement, PropertyValueMap, html } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { range } from 'lit/directives/range.js';
import { map } from 'lit/directives/map.js';

import { emit } from '@amityeko/pbc-core/dist/utilities/event';
import { setSmoothLoading } from '@amityeko/pbc-core/dist/utilities/loading';
import { withAuth } from '@amityeko/pbc-core/dist/providers/auth/consumer-mixin';

import awardIcon from '@amityeko/design-system/dist/assets/icons/award';

import '@amityeko/design-system/dist/elements/icon';
import '@amityeko/design-system/dist/elements/dialog';
import '@amityeko/design-system/dist/elements/chips-selection';
import '@amityeko/design-system/dist/components/empty-view';
import '@amityeko/design-system/dist/elements/skeleton';

import type AmityekoDialog from '@amityeko/design-system/dist/elements/dialog';
import { ChipsSelectionOption } from '@amityeko/design-system/dist/elements/chips-selection/types';

import '../card';

import { BadgeResponse, BadgeList } from './types';

import styles from './styles';

@customElement('rnr-badges-dashboard')
export default class RnRBadgesDashboard extends withAuth(LitElement) {
  static styles = styles;

  @query('#dialog') dialog!: AmityekoDialog;

  @state()
  selectedCardId?: string;

  @state()
  selectedCategoryId?: string;

  @state()
  categoryList?: ChipsSelectionOption[];

  @state()
  badgeList?: BadgeList[];

  @state()
  selectedBadge?: BadgeList;

  @state()
  isLoading: boolean = false;

  @state()
  isImageLoading: boolean = false;

  @state()
  badgeLimit?: number;

  @state()
  footerInfoText?: string;

  @state()
  isLimitationLoading: boolean = false;

  public show() {
    this.dialog.show();
  }

  protected updated(changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>) {
    if (!changedProperties.has('auth') || !this.auth) return;

    const prevAuth: typeof this.auth = changedProperties.get('auth');
    if (this.auth.isAuthenticated && !prevAuth?.isAuthenticated) {
      this._getCategoryList();
    }
  }

  private async _getCategoryList() {
    try {
      const response = await this.auth?.http?.get('/api/v1/categories');

      this.categoryList = response.map((data: any) => ({
        id: data?.id,
        value: data?.name,
        label: data?.name,
        selectionColor: data?.color,
      }));

      // call badge list api for default category
      if (this.categoryList?.length) {
        this._getBadgeList(this.categoryList[0]?.id);
      }
    } catch (e: any) {
      emit(this, 'amityeko-notification--toast', {
        detail: { message: e?.error?.message, variant: 'error' },
      });
    }
  }

  private _produceImagePromise(badge?: BadgeResponse): Promise<BadgeList | null> {
    if (!this.auth?.http || !badge || !badge.thumbnail) return Promise.resolve(null);

    this.isImageLoading = true;

    return new Promise(resolve => {
      this.auth?.http?.media(`/api/v1/media/images/${badge.thumbnail}`).then((src?: string) => {
        if (src) {
          resolve({
            id: badge?.id,
            imageSrc: src,
            cardTitle: badge?.name,
            points: badge?.rewards[0]?.value,
            updatedAt: badge?.updatedAt,
          });
          setSmoothLoading(1000).then((loadingState: boolean) => {
            this.isImageLoading = loadingState;
          });
        } else {
          resolve(null);
          setSmoothLoading(1000).then((loadingState: boolean) => {
            this.isImageLoading = loadingState;
          });
        }
      });
    });
  }

  private async _getBadgeList(categoryId: string) {
    this.badgeList = undefined;
    this.isLoading = true;
    try {
      //call badge list api
      const response = await this.auth?.http?.get('/api/v1/badges', {
        categoryId,
      });

      if (!Array.isArray(response?.data)) return;

      const transformedPromises = response?.data.map((badge: BadgeResponse) =>
        this._produceImagePromise(badge),
      );

      Promise.all(transformedPromises).then((processBadges: Array<BadgeList | null> = []) => {
        this.badgeList = processBadges.filter((badge): badge is BadgeList => Boolean(badge));

        setSmoothLoading(1000).then((loadingState: boolean) => {
          this.isLoading = loadingState;
        });

        if (this.badgeList.length > 0) {
          this.selectedCardId = this.badgeList[0].id;
          this._getBadgeLimitation(this.selectedCardId);
          this.selectedBadge = this.badgeList[0];
        }
      });
    } catch (e: any) {
      setSmoothLoading(1000).then((loadingState: boolean) => {
        this.isLoading = loadingState;
      });

      emit(this, 'amityeko-notification--toast', {
        detail: { message: e?.error?.message, variant: 'error' },
      });
    }
  }

  private async _getBadgeLimitation(badgeId: string) {
    try {
      this.footerInfoText = undefined;
      this.isLimitationLoading = true;
      //call badge limitation api
      const response = await this.auth?.http?.get(
        `/api/v1/badges/${badgeId}/limitations/remaining`,
      );
      this.badgeLimit = response?.remainingRecognitions;

      if (this.badgeLimit === 0) {
        this.footerInfoText =
          'You have exhausted the limit to send this badge. Please contact admin or select other badges';
        //@ts-ignore
      } else if (this.badgeLimit > 0) {
        //@ts-ignore
        const text = this.badgeLimit > 1 ? 'recognitions' : 'recognition';
        this.footerInfoText = `You have ${this.badgeLimit} ${this.selectedBadge?.cardTitle} ${text} to send.`;
      } else {
        this.footerInfoText = undefined;
      }

      setSmoothLoading(1000).then((loadingState: boolean) => {
        this.isLimitationLoading = loadingState;
      });
    } catch (e: any) {
      setSmoothLoading(1000).then((loadingState: boolean) => {
        this.isLimitationLoading = loadingState;
      });
      emit(this, 'amityeko-notification--toast', {
        detail: { message: e?.error?.message, variant: 'error' },
      });
    }
  }

  private _onCardClick(badge: BadgeList) {
    this.selectedCardId = badge?.id;
    this._getBadgeLimitation(badge?.id);
    this.selectedBadge = badge;
  }

  private _onSelectCategoryFilter(e: CustomEvent) {
    this.selectedCategoryId = e.detail?.id;

    if (!this.selectedCategoryId) return;

    this._getBadgeList(this.selectedCategoryId);
  }

  private _onTriggerClick() {
    this.dialog.show();
  }

  private _onConfirmContinue() {
    this.dialog.hide();
    emit(this, 'rnr-click-continue', { detail: { ...this.selectedBadge } });
  }

  private _renderLoading() {
    return html`
      ${map(
        range(6),
        () => html`
          <amityeko-skeleton
            .width=${'133px'}
            .height=${'166px'}
            .borderRadius=${'20px'}
          ></amityeko-skeleton>
        `,
      )}
    `;
  }

  private _renderContent() {
    return html`
      ${this.badgeList?.length
        ? html`${repeat(
            this.badgeList,
            card => card?.id,
            card => html`
              <rnr-client-badge-card
                .selected=${card.id === this.selectedCardId}
                .imageSrc=${card?.imageSrc}
                .cardTitle=${card?.cardTitle}
                .points=${card?.points}
                icon
                ?isLoading=${this.isImageLoading}
                @click=${() => this._onCardClick(card)}
              ></rnr-client-badge-card>
            `,
          )}`
        : html`
            <amityeko-empty-view label="No badges here" caption="Badges will be available soon!">
              <amityeko-icon slot="badge-icon">${awardIcon}</amityeko-icon>
            </amityeko-empty-view>
          `}
    `;
  }

  protected render() {
    return html`
      <slot @click=${this._onTriggerClick}></slot>
      <amityeko-dialog
        id="dialog"
        .label=${'Select Badge'}
        ?shouldShowFooter=${true}
        ?isDisabled=${this.badgeLimit === 0 || this.isLimitationLoading}
        ?isFooterInfoTextLoading=${this.isLimitationLoading}
        .footerInfoText=${this.footerInfoText}
        .confirmButtonText=${this.categoryList?.length ? 'Continue' : undefined}
        @amityeko-confirm=${this._onConfirmContinue}
      >
        <div class="content">
          ${this.categoryList?.length
            ? html`
                <amityeko-chips-selection
                  .options=${this.categoryList}
                  @amityeko-select=${this._onSelectCategoryFilter}
                  .selectedId=${this.selectedCategoryId || this.categoryList[0]?.id}
                ></amityeko-chips-selection>
                <div class="cards">
                  ${this.isLoading ? this._renderLoading() : this._renderContent()}
                </div>
              `
            : html`
                <amityeko-empty-view
                  label="No badges here"
                  caption="Badges will be available soon!"
                >
                  <amityeko-icon slot="badge-icon">${awardIcon}</amityeko-icon>
                </amityeko-empty-view>
              `}
        </div>
      </amityeko-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rnr-badges-dashboard': RnRBadgesDashboard;
  }
}
